import { useEffect, useState } from "react";
import api from "../../utils/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {LogOut, ArrowLeft} from 'lucide-react';


const AdminUsers = () => {
const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // add / edit
  const [formOpen, setFormOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "admin",
    active: true,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  /* ================= STATUS ================= */
  const toggleStatus = async (id, active) => {
    try {
      await api.put(`/users/${id}/status`, { active: !active });
      toast.success("User status updated");
      fetchUsers();
    } catch {
      toast.error("Status update failed");
    }
  };

  /* ================= ADD ================= */
  const openAdd = () => {
    setIsEdit(false);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "admin",
      active: true,
    });
    setFormOpen(true);
  };

  /* ================= EDIT ================= */
  const openEdit = (u) => {
    setIsEdit(true);
    setSelectedUser(u);
    setFormData({
      name: u.name,
      email: u.email,
      password: "",
      role: u.role,
      active: u.active,
    });
    setFormOpen(true);
  };

  const submitForm = async () => {
    try {
      if (isEdit) {
        await api.put(`/users/${selectedUser.id}`, formData);
        toast.success("User updated");
      } else {
        await api.post("/users", formData);
        toast.success("New admin added");
      }

      setFormOpen(false);
      fetchUsers();
    } catch {
      toast.error("Save failed");
    }
  };

  /* ================= DELETE ================= */
  const openDeletePopup = (user) => {
    setSelectedUser(user);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success("User deleted");
      setDeleteOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) return <div className="p-10">Loading users...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
         <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-heading font-bold text-gradient">CodeSky Admin</h1>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 text-slate-600 hover:text-primary transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 pt-9 pb-10">
         {/* 🔙 Back Button */}
<div className="mb-4">
  <button
    onClick={() => navigate(-1)}
    className="flex items-center gap-2 text-slate-600 hover:text-primary transition-colors"
  >
    <ArrowLeft className="w-4 h-4" />
    <span className="text-sm font-medium">Back</span>
  </button>
</div>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Admin Management</h1>
          <Button onClick={openAdd}>+ Add Admin</Button>
        </div>

        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full min-w-[800px] text-sm">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="p-4 text-left">Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th className="text-right pr-4">Actions</th>
                </tr>
              </thead>

              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t hover:bg-slate-50">
                    <td className="p-4 font-medium">{u.name}</td>
                    <td>{u.email}</td>
                    <td className="capitalize">{u.role}</td>
                    <td>
                      {u.active ? (
                        <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">
                          Active
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">
                          Disabled
                        </span>
                      )}
                    </td>

                    <td className="text-right pr-4 space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(u)}
                      >
                        Edit
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleStatus(u.id, u.active)}
                      >
                        {u.active ? "Disable" : "Enable"}
                      </Button>

                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => openDeletePopup(u)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ADD / EDIT MODAL */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isEdit ? "Edit Admin" : "Add New Admin"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              placeholder="Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
            />

            <Input
              placeholder="Email"
              disabled={isEdit}
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />

            <Input
              type="password"
              placeholder={isEdit ? "New password (optional)" : "Password"}
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
            />

            <select
              className="w-full border rounded px-3 py-2"
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
            >
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
            </select>

            <Button className="w-full" onClick={submitForm}>
              {isEdit ? "Update Admin" : "Create Admin"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DELETE MODAL */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Admin?</DialogTitle>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4">
              <p>
                Delete admin <strong>{selectedUser.email}</strong>?
              </p>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDelete}>
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
